import { z } from 'zod'
import type { ApiClient } from './client'
import { ackEnvelope, assertAck, flatCountEnvelope } from './envelope'
import { platformUsersResponse } from './platform-users'
import type { PlatformUser } from './platform-users'

/**
 * Settings → Organizations (docs/settings-requirements.md §6).
 *
 * The tenancy root: an organization owns its people, carries its own
 * authentication policy, and is associated with the tenants it may reach.
 * `/lc/mgmt/org/*`, flat — never cluster-scoped.
 *
 * Three things worth knowing before reading the bindings, because each one is
 * a place a plausible-looking guess is wrong:
 *
 *  1. PAGING KEYS DIFFER PER ENDPOINT, in the same section. The directory and
 *     the association list take `page` / `size`; the org-scoped user list
 *     takes `pageno` / `pagesize`. This is not tidied — reproducing it is the
 *     only thing that works.
 *  2. THE STATUS WRITE HAS ITS OWN KEY AND ITS OWN VOCABULARY. A row reads
 *     `enabled: boolean`; the write takes `{ org_status: 'enabled' | 'disabled' }`
 *     — not `status`, and not a boolean. That is a fourth status vocabulary in
 *     Settings, after the user row's `active`/`inactive`, the user write's
 *     `enabled`/`disabled`, and AD's path verbs `enable`/`disable`.
 *  3. THE ASSOCIATION LIST NESTS. `data` is an OBJECT holding `tenantDetails`,
 *     not the array itself — a fourth envelope shape, so it gets its own
 *     schema rather than borrowing `flatCountEnvelope`.
 */

/* --------------------------------------------------------- the directory */

/**
 * The identity of an organization — what any consumer needs to ADDRESS one.
 *
 * `orgId`, `orgName` and `enabled` together are what make the Users writes
 * possible at all: a user row carries only `orgName`, and two of its three
 * writes need an id. That is why this schema is the narrow one and
 * `organizationRow` below extends it — Users depends on this much and no more,
 * so the Organizations list can grow columns without touching Users.
 */
export const organization = z
  .object({
    orgId: z.string(),
    orgName: z.string().default(''),
    /** Whether the organization itself is enabled — the precondition on enabling a user. */
    enabled: z.boolean().default(true),
    authType: z.string().default(''),
  })
  .passthrough()

export type Organization = z.output<typeof organization>

/* ---------------------------------------------------------------- the row */

/**
 * A directory row — `POST /lc/mgmt/org/list`.
 *
 * Extends the base `organization` in `platform-users.ts` rather than restating
 * it. That base is the resolution shape the Users writes depend on (`orgId`,
 * `orgName`, `enabled`); this adds only what the Organizations LIST renders.
 * One schema, so the two cannot drift.
 *
 * The two count fields are keyed by their DISPLAY names, spaces and capitals
 * included: `'Organization Admin'` and `'General User'`. They are absent on a
 * row with none of that user type, which is why both default to 0 — the legacy
 * screen patched the same hole client-side on every row it received.
 */
export const organizationRow = organization.extend({
  'Organization Admin': z.number().default(0),
  'General User': z.number().default(0),
})

export type OrganizationRow = z.output<typeof organizationRow>

/**
 * The directory response, for BOTH readers.
 *
 * One schema rather than a narrow one for Users and a wide one for this list:
 * `organizationRow`'s count fields default to 0, so it parses a payload that
 * omits them. Two schemas over one endpoint is how the two readers drift.
 */
export const organizationsResponse = flatCountEnvelope(organizationRow)

/**
 * One organization in full — `GET /lc/mgmt/org/{orgId}/org-details`.
 *
 * `data` is an OBJECT here. Worth stating because the sibling
 * `GET /lc/mgmt/users/{userId}` returns an ARRAY OF ONE for the equivalent
 * read, and assuming either shape holds for the other yields an empty screen
 * rather than an error.
 *
 * `LastUpdated` is epoch SECONDS and 0 means never — the same trap as a user's
 * `last_login`, so it gets the same treatment (`orgUpdatedDate`).
 */
export const organizationDetail = organizationRow.extend({
  LastUpdated: z.number().nullable().default(null),
  /** Distinct users who signed in over the last 7 days. Server-computed. */
  orgUserActivityCount: z.number().nullable().default(null),
  sessionTimeout: z.union([z.number(), z.string()]).nullable().default(null),
})

export type OrganizationDetail = z.output<typeof organizationDetail>

export const organizationDetailResponse = z.object({
  status: z.string().optional(),
  message: z.string().optional(),
  data: organizationDetail,
})

/* ------------------------------------------------------- tenant association */

/**
 * One tenant an organization is associated with.
 *
 * `tenantAssociation` arrives as prose — the legacy screen matched the
 * SUBSTRINGS 'Primary Organization' and 'Secondary Organization' rather than
 * comparing whole values, so the exact wording is not a vocabulary this
 * console may rely on. `associationKind` below reproduces that reading and
 * keeps the raw string for anything it does not recognise.
 */
export const orgTenant = z
  .object({
    tenantName: z.string().default(''),
    tenantAssociation: z.string().default(''),
  })
  .passthrough()

export type OrgTenant = z.output<typeof orgTenant>

/**
 * The association envelope: `{ status, count, data: { tenantDetails: [...] } }`.
 *
 * NESTED, unlike every other list in this section. `data.tenantDetails`, not
 * `data`. Parsed strictly for that reason — a permissive read of the flat
 * shape would render "no tenants" for an organization that has them, and
 * "no tenants" is the answer that makes a disable look safe.
 */
export const orgAssociationResponse = z.object({
  count: z.number().default(0),
  status: z.string().optional(),
  message: z.string().optional(),
  data: z
    .object({ tenantDetails: z.array(orgTenant).default([]) })
    .passthrough(),
})

export type AssociationKind = 'primary' | 'secondary' | 'other'

/** Read the association prose, substring-wise, as the legacy screen did. */
export function associationKind(t: OrgTenant): AssociationKind {
  const v = t.tenantAssociation
  if (v.includes('Primary Organization')) return 'primary'
  if (v.includes('Secondary Organization')) return 'secondary'
  return 'other'
}

/* --------------------------------------------------------------- vocabulary */

/** The authType vocabulary, served by type_list — the same shape as userTypes. */
export const authTypesResponse = z.object({
  status: z.string().optional(),
  message: z.string().optional(),
  data: z.array(z.string()).default([]),
})

/**
 * `2FA` on the wire is `MFA` on screen.
 *
 * The legacy list and detail both rendered `authType.replace('2FA', 'MFA')`,
 * and the FILTER sent the raw value back. So the substitution is presentation
 * only and must never reach a payload — which is why it lives here as a
 * display function and not as a normalisation of the parsed value.
 */
export function authTypeLabel(authType: string): string {
  return authType.replace('2FA', 'MFA')
}

/* ------------------------------------------------------------------ status */

export const ORG_ENABLE = 'enabled'
export const ORG_DISABLE = 'disabled'

export function isOrgEnabled(o: Pick<Organization, 'enabled'>): boolean {
  return o.enabled !== false
}

/**
 * The value to WRITE to flip this organization.
 *
 * Takes the ORGANIZATION, not a status, for the same reason `nextUserStatus`
 * does: the row's state is a BOOLEAN and the write takes a STRING, so there is
 * no field a call site could correctly forward. Removing the temptation is
 * cheaper than catching it in review.
 */
export function nextOrgStatus(o: Pick<Organization, 'enabled'>): string {
  return isOrgEnabled(o) ? ORG_DISABLE : ORG_ENABLE
}

/** Epoch SECONDS to a Date. 0 and null both mean never updated. */
export function orgUpdatedDate(o: Pick<OrganizationDetail, 'LastUpdated'>): Date | null {
  const t = o.LastUpdated
  return typeof t === 'number' && t > 0 ? new Date(t * 1000) : null
}

/** Total people, as the legacy list totalled them: the two counted types. */
export function orgUserTotal(o: OrganizationRow): number {
  return o['Organization Admin'] + o['General User']
}

/* -------------------------------------------------------------------- api */

/**
 * One entry of `add_filter`, and it is NOT the shape the other lists use.
 *
 * Tokens and plugins send `addfilters: [{ filter_key, filter_condition,
 * filter_value, filter_operand }]`. This list sends `add_filter` — singular,
 * as the platform user list does — holding SINGLE-KEY OBJECTS keyed by the
 * field itself: `[{ authType: 'SSO' }, { enabled: true }]`. No operand, no
 * condition, and the value keeps its JSON type (`enabled` goes as a boolean,
 * matching the row).
 *
 * Three filter dialects in one Settings area. Reproduced per endpoint.
 */
export type OrgFilter = Record<string, unknown>

export interface OrganizationsQuery {
  page?: number
  size?: number
  query?: string
  filters?: OrgFilter[]
}

export interface OrganizationsPage {
  orgs: OrganizationRow[]
  total: number
}

export interface OrgUsersQuery {
  orgId: string
  pageNo?: number
  pageSize?: number
  query?: string
  filters?: OrgFilter[]
}

export interface OrgUsersPage {
  users: PlatformUser[]
  total: number
}

export interface OrgTenantsQuery {
  orgId: string
  page?: number
  size?: number
  query?: string
}

export interface OrgTenantsPage {
  tenants: OrgTenant[]
  total: number
}

export interface OrgCreateInput {
  orgName: string
  authType: string
  sessionTimeout: number
}

export interface OrgUpdateInput extends OrgCreateInput {
  orgId: string
}

export interface OrganizationsApi {
  /** The directory, paged. `page`/`size` — NOT `pageno`/`pagesize`. */
  list: (q?: OrganizationsQuery) => Promise<OrganizationsPage>
  /** One organization in full. `data` is an object, not an array of one. */
  detail: (orgId: string) => Promise<OrganizationDetail>
  /** The authType vocabulary. Served, unlike token capabilities (G1). */
  authTypes: () => Promise<string[]>
  /** This organization's people. `pageno`/`pagesize` here, and an `orgId`. */
  users: (q: OrgUsersQuery) => Promise<OrgUsersPage>
  /** The tenants this organization reaches. Nested envelope. */
  tenants: (q: OrgTenantsQuery) => Promise<OrgTenantsPage>
  /** Create. Returns the new organization's id — the only place it is served. */
  create: (input: OrgCreateInput) => Promise<string | null>
  update: (input: OrgUpdateInput) => Promise<void>
  /** Enable/disable. Pass `nextOrgStatus(org)`, never a field off the row. */
  setStatus: (input: { orgId: string; status: string }) => Promise<void>
}

/**
 * `POST /lc/mgmt/org/` — note the TRAILING SLASH.
 *
 * Without it the request is `/lc/mgmt/org`, which this API does not serve as a
 * create; per rule S1 the SPA answers a wrong path with its own HTML at HTTP
 * 200, so the failure would arrive as a parse error on a "successful" create.
 */
const CREATE_PATH = '/lc/mgmt/org/'

/**
 * What create's ack carries back, beyond `{ status, message }`.
 *
 * `data.orgId` is the ONLY place a new organization's id is served — there is
 * no read that finds it by the name just submitted without inheriting G13's
 * duplicate-name ambiguity. It is optional here because a bare-200 ack is an
 * accepted shape (see `assertAck`), and a create that succeeded without
 * returning an id is a success the screen must still report.
 */
const createResponse = z
  .object({
    status: z.string().optional(),
    message: z.string().optional(),
    data: z.object({ orgId: z.string().optional() }).passthrough().optional(),
  })
  .passthrough()

export function createOrganizationsApi(client: ApiClient): OrganizationsApi {
  return {
    list: async ({ page = 1, size = 50, query = '', filters = [] } = {}) => {
      const res = await client.request('POST', '/lc/mgmt/org/list', {
        // `page`/`size` — the org-scoped user list one method down takes
        // `pageno`/`pagesize` against the same prefix. Both are reproduced as
        // found; neither is a typo to fix.
        body: { page, size, query, add_filter: filters },
        schema: organizationsResponse,
      })
      return { orgs: res.data, total: res.count }
    },

    detail: async (orgId) => {
      const res = await client.request(
        'GET',
        `/lc/mgmt/org/${encodeURIComponent(orgId)}/org-details`,
        { schema: organizationDetailResponse },
      )
      return res.data
    },

    authTypes: async () => {
      const res = await client.request('GET', '/lc/mgmt/type_list?type=authTypes', {
        schema: authTypesResponse,
      })
      return res.data
    },

    /**
     * `POST /lc/mgmt/org/user-list` WITH an `orgId`.
     *
     * The same endpoint Settings → Users calls flat. It accepts an `orgId` in
     * the BODY and scopes to that organization — so the per-org view is a
     * real server-side filter, not a client-side narrowing of the flat list,
     * and it identifies its organization by ID rather than by name. G13's
     * duplicate-name ambiguity does not reach this tab.
     */
    users: async ({ orgId, pageNo = 1, pageSize = 50, query = '', filters = [] }) => {
      const res = await client.request('POST', '/lc/mgmt/org/user-list', {
        body: { pageno: pageNo, pagesize: pageSize, query, orgId, add_filter: filters },
        schema: platformUsersResponse,
      })
      return { users: res.data, total: res.count }
    },

    tenants: async ({ orgId, page = 1, size = 50, query = '' }) => {
      const res = await client.request(
        'POST',
        `/lc/mgmt/org/${encodeURIComponent(orgId)}/association`,
        { body: { page, size, query, add_filter: [] }, schema: orgAssociationResponse },
      )
      return { tenants: res.data.tenantDetails, total: res.count }
    },

    create: async ({ orgName, authType, sessionTimeout }) => {
      const res = await client.request('POST', CREATE_PATH, {
        body: { orgName, authType, sessionTimeout },
        schema: createResponse,
      })
      assertAck(res, 'Creating the organization')
      return res.data?.orgId ?? null
    },

    update: async ({ orgId, orgName, authType, sessionTimeout }) => {
      const res = await client.request('PUT', `/lc/mgmt/org/${encodeURIComponent(orgId)}`, {
        body: { orgName, authType, sessionTimeout },
        schema: ackEnvelope,
      })
      assertAck(res, 'Updating the organization')
    },

    setStatus: async ({ orgId, status }) => {
      const res = await client.request(
        'POST',
        `/lc/mgmt/org/${encodeURIComponent(orgId)}/change_status`,
        // `org_status`, not `status`. The one write in Settings whose status
        // key is not called `status`.
        { body: { org_status: status }, schema: ackEnvelope },
      )
      assertAck(res, 'Changing the organization’s status')
    },
  }
}

/* ------------------------------------------------- resolving one by name */

/**
 * What a lookup of a user's organization concluded.
 *
 * A user row carries `orgName` and no id, but delete needs the id IN ITS PATH.
 * The resolution is therefore explicit and its outcome is a value, not a
 * boolean — because "we could not tell" and "there is no such org" have to
 * reach the operator differently, and neither may quietly become a guess.
 *
 * A wrong-but-real orgId is worse than none here. `/org/undefined/user/{id}`
 * fails loudly; `/org/{some-other-real-org}/user/{id}` is a delete whose
 * target is unknown.
 */
export type OrgResolution =
  | { kind: 'resolved'; org: Organization }
  | { kind: 'none'; name: string }
  | { kind: 'ambiguous'; name: string; matches: Organization[] }

/**
 * Pick the organization a user belongs to out of a directory query.
 *
 * The match is EXACT on name, case-insensitively — the directory `query` is a
 * substring search, so "GCPD" also returns "GCPD Archive". Anything other than
 * exactly one exact match refuses rather than choosing.
 */
export function resolveOrg(name: string, candidates: readonly Organization[]): OrgResolution {
  const needle = name.trim().toLowerCase()
  if (!needle) return { kind: 'none', name }
  const exact = candidates.filter((o) => o.orgName.trim().toLowerCase() === needle)
  if (exact.length === 1) return { kind: 'resolved', org: exact[0]! }
  if (exact.length === 0) return { kind: 'none', name }
  return { kind: 'ambiguous', name, matches: exact }
}
